/**
 * Stripe Configuration
 *
 * Frontend integration with Stripe.js for payment processing.
 * Used for the pay-per-article system.
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';

// Get publishable key from environment variables
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Log warning if Stripe is not configured, but don't block the app
if (!stripePublishableKey || stripePublishableKey === 'pk_test_placeholder') {
  console.warn(
    'Stripe is not configured (VITE_STRIPE_PUBLISHABLE_KEY missing or placeholder). ' +
    'Payment features will be disabled.'
  );
}

// Singleton instance of Stripe
let stripePromise: Promise<Stripe | null>;

/**
 * Get Stripe instance (singleton pattern)
 *
 * @returns Promise that resolves to Stripe instance
 *
 * @example
 * ```tsx
 * import { getStripe } from '@/lib/stripe';
 *
 * const stripe = await getStripe();
 * const { error } = await stripe.redirectToCheckout({ sessionId });
 * ```
 */
export const getStripe = (): Promise<Stripe | null> => {
  // Return null if Stripe is not configured
  if (!stripePublishableKey || stripePublishableKey === 'pk_test_placeholder') {
    return Promise.resolve(null);
  }

  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise;
};

/**
 * Prix unitaire d'un article premium
 */
export const ARTICLE_PRICE = 5000; // 50.00 EUR (en centimes)

/**
 * Promotional codes
 */
export const PROMO_CODES = {
  AGRICULTEUR2024: 'agriculteur2024',
  ETUDIANT2024: 'etudiant2024',
  PARTENAIRE2024: 'partenaire2024',
} as const;

/**
 * Retourne le prix d'un article (prix unique)
 */
export const getArticlePrice = (_purchaseCount: number): number => {
  return ARTICLE_PRICE;
};

/**
 * Format price in cents to EUR string
 *
 * @param priceInCents - Price in cents
 * @returns Formatted price string (e.g., "50,00 €")
 *
 * @example
 * ```ts
 * formatPrice(5000);  // "50,00 €"
 * formatPrice(4000);  // "40,00 €"
 * ```
 */
export const formatPrice = (priceInCents: number): string => {
  const euros = priceInCents / 100;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(euros);
};
